import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Music, CheckCircle, Clock, Monitor, Apple, ArrowLeft, HelpCircle } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { ALL_PLAYERS } from '../../services/playerUtils';
import { ALL_PLAY_MODES, playModeConfig } from '../../services/playModes';

interface PlatformStatus {
  [playerName: string]: 'tested' | 'community' | 'untested';
}

const CATEGORY_LABELS: Record<string, string> = {
  streaming: 'Streaming Services',
  local: 'Local / Desktop Players',
  other: 'Other',
};

const STATUS_CONFIG = {
  tested: {
    label: 'Tested',
    icon: <CheckCircle className="w-4 h-4 text-green-500" />,
    className: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  },
  community: {
    label: 'Community reported',
    icon: <CheckCircle className="w-4 h-4 text-blue-500" />,
    className: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  },
  untested: {
    label: 'Untested',
    icon: <Clock className="w-4 h-4 text-gray-400" />,
    className: 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400',
  },
};

function PlatformIcon({ name }: { name: string }) {
  if (name === 'Apple Music') return <Apple className="w-5 h-5" />;
  return <Music className="w-5 h-5" />;
}

function PlatformBadge({ platform }: { platform: 'Windows' | 'Mac' }) {
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${
      platform === 'Windows'
        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
    }`}>
      {platform === 'Windows' ? <Monitor className="w-3 h-3" /> : <Apple className="w-3 h-3" />}
      {platform}
    </span>
  );
}

export default function PlatformsPage() {
  const [statuses, setStatuses] = useState<PlatformStatus>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDoc(doc(db, 'config', 'platforms'))
      .then((snap) => {
        if (snap.exists()) {
          setStatuses(snap.data() as PlatformStatus);
        }
      })
      .catch(() => {
        // Firestore unavailable — fall back to all untested
      })
      .finally(() => setLoading(false));
  }, []);

  const grouped = ALL_PLAYERS.reduce<Record<string, typeof ALL_PLAYERS>>((acc, player) => {
    if (!acc[player.category]) acc[player.category] = [];
    acc[player.category].push(player);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-3xl mx-auto px-4 py-6">
          <Link
            to="/register"
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>
          <div className="flex items-start gap-4">
            <div className="p-3 bg-primary/10 rounded-xl">
              <Music className="w-7 h-7 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Supported Players</h1>
              <p className="text-gray-500 dark:text-gray-400 mt-1">
                Each event plays through one player you choose: Spotify, Apple Music or TIDAL. Guests search
                the catalog and your buttons open the exact song or a search in that player.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8 space-y-8">

        {/* Players you can choose */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <h2 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            <Music className="w-4 h-4 text-primary" />
            Players you can choose
          </h2>
          <ul className="space-y-2">
            {ALL_PLAY_MODES.map(playModeConfig).filter(mode => mode.selectable).map(mode => (
              <li key={mode.id} className="text-sm">
                <span className="font-medium text-gray-900 dark:text-white">{mode.name}</span>
                <span className="text-gray-600 dark:text-gray-400"> — {mode.description}</span>
              </li>
            ))}
          </ul>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">
            <span className="font-medium">Automatic modes (coming later):</span>{' '}
            {ALL_PLAY_MODES.map(playModeConfig).filter(mode => !mode.selectable).map(mode => mode.name).join(' and ')}.
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            You pick the player when you create an event. It locks once the event gets its first request.
          </p>
        </div>

        {/* How it works */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <h2 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            <HelpCircle className="w-4 h-4 text-primary" />
            How Now Playing detection works
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
            The <strong className="text-gray-800 dark:text-gray-200">VoteBeats Bridge</strong> reads
            the currently playing song directly from the OS — no API keys or account connections needed —
            and updates your event's Now Playing for the crowd. Your buttons and links follow the player
            you chose for the event, not the app Bridge happens to see.
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mt-2">
            On <strong className="text-gray-800 dark:text-gray-200">Windows</strong>, this uses the
            System Media Transport Controls (SMTC) — the same API that powers the taskbar media controls.
            On <strong className="text-gray-800 dark:text-gray-200">Mac</strong>, it uses AppleScript.
          </p>
        </div>

        {/* Player grid by category */}
        <h2 className="font-semibold text-gray-900 dark:text-white">What Bridge can detect</h2>
        {(['streaming', 'local'] as const).map((category) => (
          <div key={category}>
            <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
              {CATEGORY_LABELS[category]}
            </h2>
            <div className="space-y-2">
              {(grouped[category] ?? []).map((player) => {
                const status: keyof typeof STATUS_CONFIG =
                  statuses[player.name] ?? 'untested';
                const statusCfg = STATUS_CONFIG[status];
                return (
                  <div
                    key={player.name}
                    className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center gap-4"
                  >
                    <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-gray-500 dark:text-gray-400 flex-shrink-0">
                      <PlatformIcon name={player.name} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-gray-900 dark:text-white">{player.name}</span>
                        {player.platforms.map((p) => (
                          <PlatformBadge key={p} platform={p} />
                        ))}
                      </div>
                    </div>
                    {!loading && (
                      <span className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full flex-shrink-0 ${statusCfg.className}`}>
                        {statusCfg.icon}
                        {statusCfg.label}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Legend */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Status legend</h3>
          <div className="space-y-2">
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
              <div key={key} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                {cfg.icon}
                <span className="font-medium text-gray-800 dark:text-gray-200">{cfg.label}</span>
                <span>—</span>
                <span>
                  {key === 'tested' && 'Verified by the VoteBeats team'}
                  {key === 'community' && 'Reported working by community members'}
                  {key === 'untested' && 'Detection implemented but not yet verified end-to-end'}
                </span>
              </div>
            ))}
          </div>
        </div>

        <p className="text-center text-xs text-gray-400 dark:text-gray-500 pb-4">
          Using a player that isn't listed?{' '}
          <Link to="/roadmap" className="text-primary hover:underline">Request support on the roadmap</Link>
        </p>
      </div>
    </div>
  );
}
